/**
 * App-wide MUI theme derived from designSystem.css.
 * Single source of truth: src/theme/designSystem.css (--primary, --success, --success-light, --success-dark, --font-family-inter).
 * SECURITY: Theme values are read from CSS custom properties at runtime to avoid drift; fallbacks used when document is unavailable (SSR/tests).
 */
import { createTheme } from "@mui/material/styles";
import { systemStore } from "@/store/SystemStore";

/** Read a CSS custom property from :root. Returns fallback when not in browser or value missing. */
function getCssVar(name, fallback = "") {
  if (typeof document === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name)?.trim();
  return value || fallback;
}

/** Estandarizado: chips (STRONG FIT), iconos (Ask to Offer, Buy Box), gráfica dona, botón Send Offer. Values match designSystem.css. */
export const SUCCESS_GREEN = "#0D9F6E";
export const SUCCESS_GREEN_LIGHT = "rgba(13, 159, 110, 0.15)";

/** Avisos (Add Structure): línea izquierda #F59E0B; fondo claro; texto marrón-naranja. */
export const WARNING_AMBER = "#F59E0B";
export const WARNING_AMBER_LIGHT = "#FDF3D0";
export const WARNING_AMBER_DARK = "#A06A26";

/** Base font stack for the design system (Inter first). Must match designSystem.css --font-family-inter. */
export const FONT_FAMILY_INTER = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif';

/** Top AppBar icon controls: fixed footprint so MUI default padding does not shrink the hit target. */
export const APP_BAR_TOOLBAR_ICON_BUTTON_SX = {
  width: 40,
  height: 40,
  minWidth: 40,
  minHeight: 40,
  padding: 0,
};

/** White circular app bar icon buttons (back, menu, notifications) with opaque hover feedback. */
export const APP_BAR_WHITE_ICON_BUTTON_SX = {
  ...APP_BAR_TOOLBAR_ICON_BUTTON_SX,
  backgroundColor: "#ffffff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  border: "1px solid transparent",
  transition: "background-color 200ms ease, box-shadow 200ms ease, border-color 200ms ease, transform 100ms ease",
  "&:hover": {
    backgroundColor: "#F3F0FF",
    background: "#F3F0FF",
    boxShadow: "0 8px 16px -4px rgba(0, 0, 0, 0.14), 0 4px 8px -3px rgba(157, 106, 196, 0.15)",
    borderColor: "rgba(157, 106, 196, 0.28)",
  },
  "&:active": {
    transform: "scale(0.98)",
  },
};

// ==========================================
// 1. Constantes que vienen de la rama 'dev'
// ==========================================
const NONE_TRANSITION = Object.freeze({
  transitions: {
    create: () => "none",
  },
});

const PERFORMANCE_MODE_COMPONENT_OVERRIDES = {
  MuiCssBaseline: {
    styleOverrides: {
      "*, *::before, *::after": {
        animation: "none !important",
        transition: "none !important",
      },
    },
  },
  MuiBackdrop: {
    styleOverrides: {
      root: {
        backdropFilter: "none",
        WebkitBackdropFilter: "none",
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backdropFilter: "none",
        WebkitBackdropFilter: "none",
      },
    },
  },
};

/**
 * Build MUI theme from designSystem.css custom properties. Uses fallbacks when CSS not yet applied (e.g. initial load).
 *
 * When `isPerformanceModeActive` is true (low-end tier or prefers-reduced-motion), transitions and blurred surfaces are toned down globally.
 *
 * @param {boolean} [isPerformanceModeActive] When omitted, uses `systemStore.isPerformanceModeActive`.
 * @returns {import('@mui/material/styles').Theme}
 */
export function buildAppTheme(isPerformanceModeActive = systemStore.isPerformanceModeActive) {
  const primary = getCssVar("--primary", "#9D6AC4");
  const primaryDark = getCssVar("--primary-dark", "#7c3aed");
  const primaryLight = getCssVar("--gray-200", "#e6d0f0");
  const secondary = getCssVar("--secondary", "#f3f0ff");
  const secondaryForeground = getCssVar("--secondary-foreground", "#555555");
  const success = getCssVar("--success", SUCCESS_GREEN);
  const successLight = getCssVar("--success-light", SUCCESS_GREEN_LIGHT);
  const successDark = getCssVar("--success-dark", "#0b8a5f");
  const successForeground = getCssVar("--success-foreground", "#ffffff");
  const fontFamily = getCssVar("--font-family-inter", FONT_FAMILY_INTER);

  const baseTheme = createTheme({
    palette: {
      primary: {
        main: primary,
        light: primaryLight,
        dark: primaryDark,
      },
      secondary: {
        main: secondary,
        contrastText: secondaryForeground,
      },
      success: {
        main: success,
        light: successLight,
        dark: successDark,
        contrastText: successForeground,
      },
      warning: {
        main: WARNING_AMBER,
        light: WARNING_AMBER_LIGHT,
        dark: WARNING_AMBER_DARK,
      },
    },
    typography: {
      fontFamily,
      h6: {
        fontWeight: 700,
        fontSize: "1.25rem",
        lineHeight: 1.3,
        "@media (max-width:600px)": {
          fontWeight: 700,
          fontSize: "1.1rem",
          lineHeight: 1.2,
        },
      },
    },
    components: {
      MuiTypography: {
        styleOverrides: {
          root: {
            "&.mobile-header-wrap": {
              "@media (max-width:600px)": {
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
                whiteSpace: "normal",
                overflow: "hidden",
                textOverflow: "clip",
                wordBreak: "break-word",
              },
            },
          },
        },
      },
    },
  });

  if (!isPerformanceModeActive) return baseTheme;

  return createTheme(baseTheme, {
    ...NONE_TRANSITION,
    components: PERFORMANCE_MODE_COMPONENT_OVERRIDES,
  });
}

/** Static baseline for callers that cannot observe the MobX tier (parity with legacy non-tiered palette). */
const appTheme = buildAppTheme(false);
export default appTheme;
